/**
 * 69.58 (ARCHITECTURE §5.29): a decoy flare's flight, closed-form from its pop — its velocity damped by COUNTERMEASURES.drag
 * a second (pos₀ + v₀ (1 − e^(−k t)) ÷ k), falling at `fallPeople` a second² along the planet's down at its pop, burning for
 * `burnSeconds`. Nothing is stepped: a missile chasing one asks where it is at the tick (core/antiDropshipMissile.ts), as it
 * asks for a ship. Pure (core/countermeasures.ts pops them).
 */
import type { DecoyFlare, DropshipFlight, GameState, Vec3 } from '../types';
import { COUNTERMEASURES, DECOY_ID_BASE } from '../constants/action';
import { PHYS_DT } from '../constants/physics';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dexp } from './dmath';
import { defaultOn } from './defaultOn';

const C = COUNTERMEASURES;

/** Whether `id` is a decoy flare's (DECOY_ID_BASE up). */
export const isDecoyId = (id: number): boolean => id >= DECOY_ID_BASE;

/** The salvos a ship of `payload` carries from its call (none without the switch). */
export function decoyLoad(config: Pick<GameState['config'], 'countermeasures'>, payload: DropshipFlight['payload'], craft?: DropshipFlight['craft']): number | undefined {
  return defaultOn(config, 'countermeasures') ? C.load[craft === 'heli' ? 'heli' : payload === 'gunship' ? 'gunship' : 'dropship'] : undefined;   // (69.59 step 1: a helicopter's own)
}

/** A flare `t` seconds after its pop (world units, a second); null outside its burn. */
export function flareAt(state: Pick<GameState, 'planets' | 'config'>, d: Pick<DecoyFlare, 'pos' | 'vel' | 'planetIndex'>, t: number): { pos: Vec3; vel: Vec3 } | null {
  if (t < 0 || t > C.burnSeconds) return null;
  const planet = state.planets[d.planetIndex];
  if (!planet) return null;
  const up = vecNormalize(vecSub(d.pos, planet.position)), g = C.fallPeople * PERSON_BODY_HEIGHT * personSetOf(state).k, e = dexp(-C.drag * t);
  const pos = vecSub(vecAdd(d.pos, vecScale(d.vel, (1 - e) / C.drag)), vecScale(up, 0.5 * g * t * t));
  return { pos, vel: vecSub(vecScale(d.vel, e), vecScale(up, g * t)) };
}

/** Decoy `id` at `tick` (where it burns and how it moves), or null: gone, or burnt out. */
export function decoyAt(state: Pick<GameState, 'planets' | 'config' | 'decoys'>, id: number, tick: number): { pos: Vec3; vel: Vec3 } | null {
  const d = state.decoys?.live.find(x => x.id === id);
  return d ? flareAt(state, d, (tick - d.tick) * PHYS_DT) : null;
}

/** How near the stretch `a` → `b` passes the point `p` (world units). */
export function segmentPass(a: Vec3, b: Vec3, p: Vec3): number {
  const ab = vecSub(b, a), L2 = vecDot(ab, ab);
  const t = L2 > 0 ? Math.max(0, Math.min(1, vecDot(vecSub(p, a), ab) / L2)) : 0;
  return vecLength(vecSub(vecAdd(a, vecScale(ab, t)), p));
}
